import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ImportDialog } from "@/components/ImportDialog";
import { ListActions } from "@/components/ListActions";
import { Plus, Pencil, Tags } from "lucide-react";

export default function OsTypes() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", active: true });

  const load = () => api.get("/os-types").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Nome é obrigatório");
    try {
      if (editing) await api.put(`/os-types/${editing.id}`, form); else await api.post("/os-types", form);
      toast.success("Salvo"); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleActive = (t, active) => api.put(`/os-types/${t.id}`, { name: t.name, description: t.description || "", active });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
      <PageHeader title="Tipos de OS" subtitle="Classifique as solicitações: Computador, Impressora, Rede, Sistema...">
        <ImportDialog resource="os-types" label="tipo de OS" onDone={load} />
        <Button onClick={() => { setEditing(null); setForm({ name: "", description: "", active: true }); setOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-os-type-btn"><Plus className="w-4 h-4 mr-2" /> Novo tipo</Button>
      </PageHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {rows.map((t, i) => (
          <Card key={t.id} className={`p-4 border-slate-200 dark:border-slate-800 ${t.active ? "" : "opacity-60"}`} data-testid={`os-type-card-${t.name}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-sky-500/10 flex items-center justify-center"><Tags className="w-4 h-4 text-sky-600" /></div>
                <div><h3 className="font-bold text-slate-900 dark:text-white text-sm">{t.name}</h3><p className="text-xs text-slate-500 line-clamp-1">{t.description || "—"}</p></div>
              </div>
              <Badge variant="outline" className={t.active ? "text-emerald-600 border-emerald-500/30" : "text-slate-400"}>{t.active ? "Ativo" : "Inativo"}</Badge>
            </div>
            <div className="flex gap-2 mt-3 items-center flex-wrap">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => { setEditing(t); setForm({ name: t.name, description: t.description, active: t.active }); setOpen(true); }} data-testid={`edit-os-type-${t.name}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
              <ListActions item={t} index={i} rows={rows} resource="os-types" isActive={t.active} onToggle={toggleActive} onChanged={load} testKey={`os-type-${t.name}`} />
            </div>
          </Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white dark:bg-slate-900">
          <DialogHeader><DialogTitle>{editing ? "Editar tipo" : "Novo tipo de OS"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input data-testid="os-type-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Descrição</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-emerald-600" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="os-type-active-checkbox" /> Ativo</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-os-type-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
